import React, { useEffect, useState } from 'react';
import { array, bool, object, string } from 'prop-types';
import classNames from 'classnames';

import { parse } from '../../util/urlHelpers';
import {
  Footer,
  LayoutSingleColumn,
  LayoutWrapperFooter,
  LayoutWrapperMain,
  LayoutWrapperTopbar,
  LinkTabNavHorizontal,
  Page,
  PaginationLinks,
} from '../../components';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import PrismicSectionHero from '../../components/PrismicSectionHero/PrismicSectionHero';
import CaseStudyCard from '../../components/CaseStudyCard/CaseStudyCard';
import { formatCaseStudyPrismicData } from '../../util/data';
import {
  COACHING_CASE_STUDY,
  COACHING_CULTURE_CASE_STUDY,
  MOST_RECENT_CASE_STUDY,
} from '../../marketplace-custom-config';
import { propTypes } from '../../util/types';

import css from './CaseStudyNavigationPage.module.css';

const formatDateOptions = { year: 'numeric', month: 'long', day: 'numeric' };

const CaseStudyNavigationPageComponent = props => {
  const {
    scrollingDisabled,
    intl,
    fetchCaseStudyInProgress,
    fetchCaseStudyError,
    pagination,
    caseStudies,
    newestCaseStudy,
    searchParams,
  } = props;

  const [currentTab, setCurrentTab] = useState(MOST_RECENT_CASE_STUDY);
  const { category } = parse(searchParams);

  useEffect(() => {
    if (category) {
      setCurrentTab(category);
    }
  }, [searchParams]);

  const { slug = '', postDate, writer, title, image, alt } = formatCaseStudyPrismicData(
    newestCaseStudy
  );
  const schemaTitle = intl.formatMessage({ id: 'CaseStudyNavigationPage.schemaTitle' });
  const tabs = [
    {
      text: (
        <span className={classNames({ [css.activeTab]: currentTab === MOST_RECENT_CASE_STUDY })}>
          <FormattedMessage id="CaseStudyNavigationPage.mostRecent" />
        </span>
      ),
      selected: currentTab === MOST_RECENT_CASE_STUDY,
      linkProps: {
        name: 'CaseStudyNavigationPage',
        to: { search: '?category=most_recent' },
      },
    },
    {
      text: (
        <span className={classNames({ [css.activeTab]: currentTab === COACHING_CASE_STUDY })}>
          <FormattedMessage id="CaseStudyNavigationPage.coaching" />
        </span>
      ),
      selected: currentTab === COACHING_CASE_STUDY,
      linkProps: {
        name: 'CaseStudyNavigationPage',
        to: { search: '?category=coaching' },
      },
    },
    {
      text: (
        <span className={classNames({ [css.activeTab]: currentTab === COACHING_CULTURE_CASE_STUDY })}>
          <FormattedMessage id="CaseStudyNavigationPage.coachingCulture" />
        </span>
      ),
      linkProps: {
        name: 'CaseStudyNavigationPage',
        to: { search: '?category=coaching_culture' },
      },
      selected: currentTab === COACHING_CULTURE_CASE_STUDY,
    },
  ];

  const dateAndWriter = intl.formatMessage(
    { id: 'CaseStudyNavigationPage.dateAndWriter' },
    {
      date: postDate ? postDate.toLocaleDateString('en-US', formatDateOptions) : null,
      writer,
    }
  );
  const featuredPostText = intl.formatMessage({ id: 'CaseStudyNavigationPage.featuredPost' });

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName="CaseStudyNavigationPage"
        pagination={pagination}
        pageSearchParams={parse(searchParams)}
      />
    ) : null;

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer hideSearchbar currentPage="CaseStudyNavigationPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <PrismicSectionHero linkParams={{ slug }} backgroundImage={image} alt={alt}>
            <div>
              <div className={css.title}>{title}</div>
              <div className={css.line} />
              <div className={css.dateAndWriter}>{dateAndWriter}</div>
              <div className={css.featuredPost}>{featuredPostText}</div>
            </div>
          </PrismicSectionHero>
          <LinkTabNavHorizontal tabs={tabs} className={css.tabs} />
          {fetchCaseStudyError && (
            <p className={css.error}>
              <FormattedMessage id="CaseStudyNavigationPage.error" />
            </p>
          )}
          {fetchCaseStudyInProgress ? (
            <p className={css.loading}>
              <FormattedMessage id="CaseStudyNavigationPage.loading" />
            </p>
          ) : (
            <>
              <div className={css.caseStudies}>
                {caseStudies.map(caseStudy => (
                  <CaseStudyCard
                    key={caseStudy.id}
                    caseStudy={caseStudy}
                    className={css.caseStudy}
                  />
                ))}
              </div>
              {paginationLinks}
            </>
          )}
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

CaseStudyNavigationPageComponent.defaultProps = {
  fetchCaseStudyInProgress: false,
  fetchCaseStudyError: null,
  pagination: null,
  caseStudies: [],
  scrollingDisabled: false,
  newestCaseStudy: null,
  searchParams: null,
};

CaseStudyNavigationPageComponent.propTypes = {
  fetchCaseStudyInProgress: bool,
  fetchCaseStudyError: propTypes.error,
  pagination: propTypes.pagination,
  caseStudies: array,
  scrollingDisabled: bool.isRequired,
  newestCaseStudy: object,
  searchParams: string,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const {
    fetchCaseStudyInProgress,
    fetchCaseStudyError,
    pagination,
    caseStudies,
    newestCaseStudy,
    searchParams,
  } = state.CaseStudyNavigationPage;

  return {
    fetchCaseStudyInProgress,
    fetchCaseStudyError,
    pagination,
    caseStudies,
    newestCaseStudy,
    searchParams,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const CaseStudyNavigationPage = compose(
  connect(mapStateToProps),
  injectIntl
)(CaseStudyNavigationPageComponent);

export default CaseStudyNavigationPage;
